import React, { useState } from "react";
import { useNavigate, Navigate, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { api, formatErr } from "../lib/api";
import { ArrowRight, Lock, Mail } from "lucide-react";

const QV_LOGO =
  "https://customer-assets.emergentagent.com/job_athletic-edge-19/artifacts/7jyn9i1w_IMG_2453.PNG";

export default function Login() {
  const { login, user, ready } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [params] = useSearchParams();
  const resetOk = params.get("reset") === "ok";
  const [forgot, setForgot] = useState(params.get("forgot") === "1");
  const [sent, setSent] = useState(false);

  if (ready && user) {
    return <Navigate to={user.role === "admin" ? "/app/admin" : user.role === "prospect" ? "/app/incorporacion" : "/app/dashboard"} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email.trim().toLowerCase(), password);
      toast.success("Bienvenido");
      navigate(u.role === "admin" ? "/app/admin" : u.role === "prospect" ? "/app/incorporacion" : "/app/dashboard");
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setLoading(false);
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setLoading(false);
    }
  };

  const linkBtn = {
    background: "none", border: "none", padding: 0, cursor: "pointer",
    fontSize: 12, color: "#a3a3a3", textDecoration: "underline", alignSelf: "flex-end",
  };

  return (
    <div className="qv-login-wrap" data-testid="login-page">
      <div className="qv-login-card">
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 24 }}>
          <img src={QV_LOGO} alt="QV" style={{ width: 44, height: 44, objectFit: "contain" }} />
          <div style={{ lineHeight: 1.1 }}>
            <div style={{ fontFamily: "Bebas Neue", fontSize: 18 }}>QUIQUE VARGAS</div>
            <div style={{ fontSize: 9, letterSpacing: "0.3em", color: "#737373", textTransform: "uppercase" }}>
              Plataforma Rendimiento
            </div>
          </div>
        </div>

        <div className="qv-overline" style={{ marginBottom: 8 }}>Acceso</div>
        <h1
          className="qv-display"
          style={{ fontSize: "2.6rem", lineHeight: 0.95, marginBottom: 24 }}
        >
          ENTRA Y<br/>
          <span style={{ color: "var(--qv-red)" }}>RINDE.</span>
        </h1>

        {resetOk && !forgot && (
          <div data-testid="login-reset-success" style={{ border: "1px solid rgba(34,197,94,0.4)", background: "rgba(34,197,94,0.08)", color: "#86efac", padding: "10px 14px", fontSize: 13, marginBottom: 16 }}>
            Contraseña actualizada. Ya puedes entrar con tu nueva contraseña.
          </div>
        )}

        {forgot ? (
          sent ? (
            <div data-testid="forgot-sent" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <p style={{ color: "#d4d4d4", fontSize: 14, lineHeight: 1.5 }}>
                Si <b>{email}</b> tiene cuenta, recibirás un email con un enlace para crear una nueva contraseña. El enlace caduca en 1 hora.
              </p>
              <button type="button" style={linkBtn} onClick={() => { setForgot(false); setSent(false); }} data-testid="forgot-back-login">
                ← Volver a iniciar sesión
              </button>
            </div>
          ) : (
            <form onSubmit={submitForgot} style={{ display: "flex", flexDirection: "column", gap: 16 }} data-testid="forgot-form">
              <p style={{ color: "#a3a3a3", fontSize: 13 }}>Escribe tu email y te enviaremos un enlace para restablecer tu contraseña.</p>
              <div className="qv-field">
                <label>Email</label>
                <div style={{ position: "relative" }}>
                  <Mail size={16} style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: "#737373" }} />
                  <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                    style={{ paddingLeft: 38 }} placeholder="tu@email.com" autoComplete="email" data-testid="forgot-email" />
                </div>
              </div>
              <button type="submit" disabled={loading} className="qv-btn-primary" style={{ justifyContent: "center" }} data-testid="forgot-submit">
                {loading ? "ENVIANDO..." : "ENVIAR ENLACE"}
              </button>
              <button type="button" style={linkBtn} onClick={() => setForgot(false)} data-testid="forgot-cancel">
                ← Volver a iniciar sesión
              </button>
            </form>
          )
        ) : (
        <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div className="qv-field">
            <label>Email</label>
            <div style={{ position: "relative" }}>
              <Mail size={16} style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: "#737373" }} />
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={{ paddingLeft: 38 }}
                placeholder="tu@email.com"
                autoComplete="email"
                data-testid="login-email"
              />
            </div>
          </div>
          <div className="qv-field">
            <label>Contraseña</label>
            <div style={{ position: "relative" }}>
              <Lock size={16} style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: "#737373" }} />
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={{ paddingLeft: 38 }}
                placeholder="••••••••"
                autoComplete="current-password"
                data-testid="login-password"
              />
            </div>
          </div>
          <button type="button" style={{ ...linkBtn, marginTop: -6 }} onClick={() => setForgot(true)} data-testid="login-forgot-link">
            ¿Olvidaste tu contraseña?
          </button>

          <button
            type="submit"
            disabled={loading}
            className="qv-btn-primary"
            style={{ justifyContent: "center", marginTop: 6 }}
            data-testid="login-submit"
          >
            {loading ? "ENTRANDO..." : "ENTRAR"}
            {!loading && <ArrowRight size={16} />}
          </button>
        </form>
        )}

        <Link
          to="/"
          style={{
            display: "block", textAlign: "center", marginTop: 24,
            fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase",
            color: "#737373", textDecoration: "none",
          }}
          data-testid="login-back-home"
        >
          ← Volver a la web
        </Link>
      </div>
    </div>
  );
}
